import { CardDetail, drawerById, fileCard, foilType, formatUsd, priceFor, spotLabel, type Card, type CardFace } from '@mtg/shared';
import { Link } from 'wouter';
import { api } from '../api.ts';
import { CardImage } from '../components/CardImage.tsx';
import { FINISH_LABEL, plural } from '../format.ts';
import { useApi } from '../useApi.ts';

/** One card: every copy you own in any printing, and where each one is. */
export function CardPage({ id }: { id: string }) {
  const { data, error } = useApi(() => api(`/api/cards/${id}`, CardDetail), [id]);
  if (error) return <p className="error">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  const { card, copies } = data;
  const owned = copies.reduce((n, c) => n + c.quantity, 0);

  return (
    <div className="card-page">
      <div className="card-page-images">
        <CardImage card={card} size="large" />
        {card.imageBack && <CardImage card={card} back />}
      </div>
      <div className="stack">
        <h1>{card.name}</h1>
        <p className="muted">
          {card.setName} ({card.setCode.toUpperCase()} {card.collectorNumber}) · {card.rarity}
          {card.releasedAt && ` · ${card.releasedAt.slice(0, 4)}`} ·{' '}
          <a href={card.scryfallUri} target="_blank" rel="noreferrer">
            Scryfall
          </a>
        </p>
        <FilingSpot card={card} />
        {card.faces.map((face) => (
          <FaceText key={face.name} face={face} />
        ))}
        <Facts card={card} />
        <h2>You own {plural(owned, 'copy', 'copies')}</h2>
        {copies.length === 0 ? (
          <p className="muted">None in the collection.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Where</th>
                <th>Printing</th>
                <th className="num">Qty</th>
                <th className="num">Each</th>
              </tr>
            </thead>
            <tbody>
              {copies.map((c) => {
                const each = priceFor(c.card, c.finish);
                return (
                  <tr key={c.id}>
                    <td>
                      <Link href={`/locations/${c.locationId}`}>{c.locationName}</Link>
                    </td>
                    <td>
                      {c.card.setCode.toUpperCase()} {c.card.collectorNumber} · {foilType(c.card, c.finish)?.label ?? FINISH_LABEL[c.finish]}
                      {c.lang !== 'en' && ` · ${c.lang.toUpperCase()}`}
                      {c.condition !== 'NM' && ` · ${c.condition}`}
                    </td>
                    <td className="num">{c.quantity}</td>
                    <td className="num">{each === null ? '—' : formatUsd(each)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const stats = (f: Pick<CardFace, 'power' | 'toughness' | 'loyalty' | 'defense'>) =>
  f.power !== null && f.toughness !== null
    ? `${f.power}/${f.toughness}`
    : f.loyalty !== null
      ? `Loyalty ${f.loyalty}`
      : f.defense !== null
        ? `Defense ${f.defense}`
        : null;

function FaceText({ face }: { face: CardFace }) {
  const pt = stats(face);
  return (
    <section className="panel face">
      <p className="face-head">
        <strong>{face.name}</strong>
        {face.manaCost && <span className="mono">{face.manaCost}</span>}
      </p>
      <p className="muted">{face.typeLine}</p>
      {face.oracleText && <p className="oracle">{face.oracleText}</p>}
      {face.flavorText && <p className="flavor muted">{face.flavorText}</p>}
      {pt && <p className="pt">{pt}</p>}
    </section>
  );
}

const FORMATS = ['commander', 'standard', 'pioneer', 'modern', 'legacy', 'vintage', 'pauper', 'brawl'];
const LEGALITY: Record<string, string> = { legal: 'Legal', not_legal: '—', banned: 'Banned', restricted: 'Restricted' };

function Facts({ card }: { card: Card }) {
  const rows: [string, string][] = [];
  if (card.keywords.length) rows.push(['Keywords', card.keywords.join(', ')]);
  if (card.artist) rows.push(['Artist', card.artist]);
  if (card.edhrecRank !== null) rows.push(['EDHREC rank', `#${card.edhrecRank.toLocaleString()}`]);
  if (card.reserved) rows.push(['Reserved List', 'Yes']);
  return (
    <>
      {rows.length > 0 && (
        <dl className="facts">
          {rows.map(([k, v]) => (
            <div key={k}>
              <dt className="muted">{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      <dl className="facts legal">
        {FORMATS.filter((f) => card.legalities[f]).map((f) => (
          <div key={f} className={card.legalities[f]}>
            <dt className="muted">{f[0]!.toUpperCase() + f.slice(1)}</dt>
            <dd>{LEGALITY[card.legalities[f]!] ?? card.legalities[f]}</dd>
          </div>
        ))}
      </dl>
    </>
  );
}

/** Where this card goes in the drawers (decks aside). */
function FilingSpot({ card }: { card: Card }) {
  const spot = fileCard(card);
  return (
    <p className="filing">
      <span className="muted small">Files in</span>
      <br />
      {spot.drawerId ? (
        <strong>
          {spotLabel(spot)} <span className="muted">({drawerById(spot.drawerId)!.label})</span>
        </strong>
      ) : (
        <span className="warn">{spot.reason}</span>
      )}
    </p>
  );
}
